import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Navigate } from 'react-router-dom';
import { useDocumentation } from '@/hooks/useDocumentation';
import { SectionTree } from '@/components/documentation/SectionTree';
import { ManualViewer } from '@/components/documentation/ManualViewer';
import { ManualEditor } from '@/components/documentation/ManualEditor';
import { VersionHistory } from '@/components/documentation/VersionHistory';
import { NewVersionModal } from '@/components/documentation/NewVersionModal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, BookOpen } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

export default function CRMDocumentation() {
  const { user, userRole } = useAuth();
  const [moduleType, setModuleType] = useState<'crm' | 'charging'>('crm');
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [newVersionModalOpen, setNewVersionModalOpen] = useState(false);

  const {
    currentManual,
    sections,
    allVersions,
    isLoading,
    updateSection,
    createVersion,
    publishVersion,
    initializeManual,
    upgradeBatch,
    isUpdating,
    isCreatingVersion,
    isPublishing,
    isInitializing,
    isUpgradingBatch,
  } = useDocumentation(moduleType);

  // Only super_admin can access
  if (userRole !== 'super_admin') {
    return <Navigate to="/crm" replace />;
  }

  const selectedSection = sections.find((s) => s.id === selectedSectionId) || null;

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">📚 Handleidingen Beheer</h1>
        <Button onClick={() => setNewVersionModalOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Nieuwe Versie
        </Button>
      </div>
      <div className="space-y-6">
        {/* Module Selector */}
        <Card className="p-4">
          <div className="flex items-center gap-4">
            <BookOpen className="h-5 w-5 text-muted-foreground" />
            <div className="flex items-center gap-2 flex-1">
              <span className="text-sm font-medium">Module:</span>
              <Select value={moduleType} onValueChange={(v: any) => setModuleType(v)}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="crm">CRM Handleiding</SelectItem>
                  <SelectItem value="charging">Charging Handleiding</SelectItem>
                </SelectContent>
              </Select>
              {currentManual && (
                <span className="text-sm text-muted-foreground">v{currentManual.version}</span>
              )}
            </div>
          </div>
        </Card>

        {/* Content Upgrade Batches */}
        {currentManual && (
          <Card className="p-6">
            <h3 className="font-semibold mb-4">📝 Content Upgrade Batches</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Upgrade handleiding content met professionele documentatie (~600 woorden per sectie). Selecteer batch om specifieke secties bij te werken.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <Button
                  onClick={() => upgradeBatch(1)}
                  disabled={isUpgradingBatch}
                  variant="outline"
                  className="w-full"
                >
                  Batch 1
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  {moduleType === 'crm' 
                    ? 'Dashboard, Leads, Organisaties, Projecten'
                    : 'Dashboard, Acquisitie, Locatie Detail, Export'}
                </p>
              </div>
              <div>
                <Button
                  onClick={() => upgradeBatch(2)}
                  disabled={isUpgradingBatch}
                  variant="outline"
                  className="w-full"
                >
                  Batch 2
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  {moduleType === 'crm'
                    ? 'Notificaties, Agenda, Contacten, Offertes'
                    : 'Realisatie, Operatie, Archief'}
                </p>
              </div>
              <div>
                <Button
                  onClick={() => upgradeBatch(3)}
                  disabled={isUpgradingBatch}
                  variant="outline"
                  className="w-full"
                >
                  Batch 3
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  {moduleType === 'crm'
                    ? 'Businesscases'
                    : 'Concurrentie, Wensen'}
                </p>
              </div>
              <div>
                <Button
                  onClick={() => upgradeBatch(4)}
                  disabled={isUpgradingBatch}
                  variant="outline"
                  className="w-full"
                >
                  Batch 4
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  {moduleType === 'crm'
                    ? 'Flex-E, Battery Planner'
                    : 'To-do\'s'}
                </p>
              </div>
              <div>
                <Button
                  onClick={() => upgradeBatch(5)}
                  disabled={isUpgradingBatch}
                  variant="outline"
                  className="w-full"
                >
                  Batch 5
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  {moduleType === 'crm'
                    ? 'To-do\'s, Instellingen'
                    : '(Reserved)'}
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Main Content */}
        {isLoading ? (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <Skeleton className="h-[600px]" />
            <Skeleton className="h-[600px] lg:col-span-3" />
          </div>
        ) : !currentManual ? (
          <Card className="p-12">
            <div className="text-center space-y-4">
              <BookOpen className="h-16 w-16 mx-auto text-muted-foreground" />
              <div>
                <h3 className="text-xl font-semibold mb-2">Geen handleiding gevonden</h3>
                <p className="text-muted-foreground mb-6">
                  Er is nog geen handleiding voor de {moduleType === 'crm' ? 'CRM' : 'Charging'} module.
                  Klik op de knop hieronder om de eerste versie aan te maken met starter content.
                </p>
              </div>
              <Button 
                onClick={() => initializeManual()} 
                disabled={isInitializing}
                size="lg"
              >
                <Plus className="h-4 w-4 mr-2" />
                {isInitializing ? 'Bezig met initialiseren...' : 'Eerste Handleiding Aanmaken'}
              </Button>
            </div>
          </Card>
        ) : (
          <Tabs defaultValue="view" className="w-full">
            <TabsList className="grid w-full max-w-md grid-cols-3">
              <TabsTrigger value="view">Inzien</TabsTrigger>
              <TabsTrigger value="edit">Bewerken</TabsTrigger>
              <TabsTrigger value="versions">Versies</TabsTrigger>
            </TabsList>

            <TabsContent value="view" className="mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                <Card className="p-4">
                  <h3 className="font-semibold mb-4">Secties</h3>
                  <SectionTree
                    sections={sections}
                    selectedSectionId={selectedSectionId}
                    onSelectSection={setSelectedSectionId}
                  />
                </Card>
                <div className="lg:col-span-3">
                  <ManualViewer section={selectedSection} />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="edit" className="mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                <Card className="p-4">
                  <h3 className="font-semibold mb-4">Secties</h3>
                  <SectionTree
                    sections={sections}
                    selectedSectionId={selectedSectionId}
                    onSelectSection={setSelectedSectionId}
                  />
                </Card>
                <div className="lg:col-span-3">
                  <ManualEditor
                    section={selectedSection}
                    onSave={(sectionId, content) => updateSection({ sectionId, content })}
                    isSaving={isUpdating}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="versions" className="mt-6">
              <VersionHistory
                versions={allVersions}
                onPublish={publishVersion}
                isPublishing={isPublishing}
              />
            </TabsContent>
          </Tabs>
        )}
      </div>

      <NewVersionModal
        open={newVersionModalOpen}
        onClose={() => setNewVersionModalOpen(false)}
        currentVersion={currentManual?.version || '1.0'}
        onCreateVersion={(version, versionNotes, isMajor) =>
          createVersion({ version, versionNotes, isMajor })
        }
        isCreating={isCreatingVersion}
      />
    </>
  );
}
